import type { PrV2CommentResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';
import type { CodeContent } from '@/types/github';

import { normalizePrV2CommentBody } from '../comments/pr-v2-comment-content';
import { extractSuggestionParts } from '../comments/pr-v2-comment-utils';

import { removeMetadataFromComment } from '@/lib/ai/code-review-service/utils/metadata';

const REVIEW_THREAD_PRIORITY_ORDER = {
  P0: 0,
  P1: 1,
  P2: 2,
  P3: 3,
  P4: 4,
} as const;
const REVIEW_THREAD_PREVIEW_MAX_LENGTH = 100;

export function getFileChangeCounts(file: CodeContent): {
  additionsCount: number;
  deletionsCount: number;
} {
  return {
    additionsCount: Math.max(0, file.additions ?? 0),
    deletionsCount: Math.max(0, file.deletions ?? 0),
  };
}

export type PrV2FileStoryCommentPreview = {
  commentId: string;
  author: string;
  preview: string;
};

type FileReviewCommentArgs = {
  comments: PrV2CommentResponse[];
  reviewThreadByCommentNodeId: Map<string, PrV2ReviewThread>;
  getCommentNodeId: (comment: PrV2CommentResponse) => string | null;
};

function asPositiveInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

function getCommentRawLineValue(
  comment: Pick<PrV2CommentResponse, 'raw'> | null,
  key: 'line' | 'start_line',
): number | null {
  if (!comment || typeof comment.raw !== 'object' || comment.raw === null) {
    return null;
  }

  return asPositiveInteger((comment.raw as Record<string, unknown>)[key]);
}

function getReviewThreadRange(
  thread: PrV2ReviewThread,
  rootComment: PrV2CommentResponse | null,
): { startLine: number; endLine: number } | null {
  const line =
    asPositiveInteger(thread.line) ??
    asPositiveInteger(thread.originalLine) ??
    asPositiveInteger(rootComment?.line) ??
    getCommentRawLineValue(rootComment, 'line');
  const startLine =
    asPositiveInteger(thread.startLine) ??
    asPositiveInteger(thread.originalStartLine) ??
    getCommentRawLineValue(rootComment, 'start_line');

  if (!line || !startLine || line === startLine) {
    return null;
  }

  return {
    startLine: Math.min(startLine, line),
    endLine: Math.max(startLine, line),
  };
}

function getReviewThreadLineNumber(
  thread: PrV2ReviewThread,
  rootComment: PrV2CommentResponse | null,
): number | null {
  const range = getReviewThreadRange(thread, rootComment);
  if (range) {
    return range.startLine;
  }

  return (
    asPositiveInteger(thread.line) ??
    asPositiveInteger(thread.originalLine) ??
    asPositiveInteger(rootComment?.line) ??
    getCommentRawLineValue(rootComment, 'line')
  );
}

function toMillis(value: string | null): number {
  if (!value) {
    return 0;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function formatReviewCommentPreview(comment: PrV2CommentResponse | null): string {
  if (!comment) {
    return '(no content)';
  }

  const normalized = normalizePrV2CommentBody({
    body: comment.body ?? null,
    userLogin: comment.userLogin ?? null,
    inReplyToId: comment.inReplyToId ?? null,
  });
  const { regularContent } = extractSuggestionParts(normalized);
  const copyText = regularContent.trim();

  const preview =
    removeMetadataFromComment(copyText)
      .replace(/<!--[\s\S]*?-->/g, ' ')
      .replace(/^\s{0,3}#{1,6}\s+/gm, '')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/\s+/g, ' ')
      .trim() || 'Suggested change';

  if (preview.length <= REVIEW_THREAD_PREVIEW_MAX_LENGTH) {
    return preview;
  }

  return `${preview.slice(0, REVIEW_THREAD_PREVIEW_MAX_LENGTH - 3).trimEnd()}...`;
}

function getReviewThreadPriorityOrder(preview: string): number {
  const priorityMatch = preview.match(/^\s*(P[0-4])\s*:/i);
  if (!priorityMatch) {
    return Number.MAX_SAFE_INTEGER;
  }

  const priority = priorityMatch[1]?.toUpperCase() as keyof typeof REVIEW_THREAD_PRIORITY_ORDER;
  return REVIEW_THREAD_PRIORITY_ORDER[priority] ?? Number.MAX_SAFE_INTEGER;
}

export function getFileReviewCommentPreviews(
  args: FileReviewCommentArgs,
): PrV2FileStoryCommentPreview[] {
  const commentPreviews: {
    sortPriority: number;
    sortLine: number;
    sortCreatedAt: number;
    preview: PrV2FileStoryCommentPreview;
  }[] = [];

  args.comments.forEach((comment) => {
    const nodeId = args.getCommentNodeId(comment);
    const thread = nodeId ? args.reviewThreadByCommentNodeId.get(nodeId) : undefined;
    if (!thread || thread.isResolved || thread.isOutdated) {
      return;
    }

    const lineNumber = getReviewThreadLineNumber(thread, comment);
    const previewText = formatReviewCommentPreview(comment);

    commentPreviews.push({
      sortPriority: getReviewThreadPriorityOrder(previewText),
      sortLine: lineNumber ?? Number.MAX_SAFE_INTEGER,
      sortCreatedAt: toMillis(comment.createdAtGithub ?? comment.updatedAtGithub),
      preview: {
        commentId: comment.githubCommentId,
        author: comment.userLogin?.trim() || 'Unknown',
        preview: previewText,
      },
    });
  });

  return commentPreviews
    .sort((left, right) => {
      if (left.sortPriority !== right.sortPriority) {
        return left.sortPriority - right.sortPriority;
      }

      if (left.sortLine !== right.sortLine) {
        return left.sortLine - right.sortLine;
      }

      if (left.sortCreatedAt !== right.sortCreatedAt) {
        return left.sortCreatedAt - right.sortCreatedAt;
      }

      return left.preview.preview.localeCompare(right.preview.preview);
    })
    .map(({ preview }) => preview);
}

export function formatReviewCommentsLabel(count: number): string {
  return `${count} comment${count === 1 ? '' : 's'}`;
}
